import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PunchupLoadMore } from 'features/common/components/Punchup/composites/tables/PunchupLoadMore';
import {
	PunchupTableContainer,
	PunchupHeaderRow,
	PunchupColumnHeader,
	PunchupDataRow,
	PunchupDateTimeCell,
	PunchupEntityCell,
	PunchupNameCell,
	PunchupTableEmptyRow,
	PunchupTableLoadingRow,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { QueryResultProps, ResponsesData, SortField, SortOrder } from 'features/CustomFieldsResponses/types';
import { formatDate } from 'features/common/utils/dateUtils';
import { SYSTEM_FIELD_NAMES } from 'features/PunchupPages/CustomFieldsResponse/components/PunchupResponsesTable/constants';
import { useResizableColumns } from 'features/common/hooks/useResizableColumns';
import { VIEW_RESPONSES_KEYS } from './constants';

interface FlatRow {
	key: string;
	date: string;
	question: string;
	answer: string;
	fullName: string;
	email: string;
	phone: string;
}

interface PunchupViewResponsesTableProps {
	responsesQueryObject: QueryResultProps;
	selectedQuestion: string | null;
	sortBy: SortField | null;
	order: SortOrder;
	onSort: (field: SortField) => void;
	/** True while an already-loaded table re-fetches — dims the table with a spinner overlay. */
	isRefetching?: boolean;
}

const flattenResponses = (pages: ResponsesData[][], selectedQuestion: string | null): FlatRow[] => {
	const rows: FlatRow[] = [];
	pages.forEach((page) => {
		page.forEach((data) => {
			data.responses
				.filter((r) => !SYSTEM_FIELD_NAMES.includes(r.custom_field.name))
				.filter((r) => !selectedQuestion || r.custom_field.name === selectedQuestion)
				.forEach((r) => {
					const answer = r.choice_id > 0 ? r.choice.name : r.string_value || '-';
					rows.push({
						key: `${data.order_id}_${data.ticket_id}_${r.custom_field.id}`,
						date: data.date,
						question: r.custom_field.name,
						answer,
						fullName: data.full_name || '-',
						email: data.email || '-',
						phone: data.phone_number || '-',
					});
				});
		});
	});
	return rows;
};

export const PunchupViewResponsesTable: FC<PunchupViewResponsesTableProps> = ({
	responsesQueryObject,
	selectedQuestion,
	sortBy,
	order,
	onSort,
	isRefetching,
}) => {
	const { t } = useTranslation();
	const { fetchNextPage, hasNextPage, data, isLoading, isFetchingNextPage } = responsesQueryObject;

	const flatRows = useMemo(
		() => (data ? flattenResponses(data.pages, selectedQuestion) : []),
		[data, selectedQuestion]
	);

	const keys = useMemo(() => [...VIEW_RESPONSES_KEYS], []);
	const { gridColumns, setColumnWidth } = useResizableColumns(keys);

	const sortDir = (field: SortField) => {
		if (sortBy !== field) return null;
		return order === 'asc' ? 'asc' : ('desc' as const);
	};

	return (
		<>
			<PunchupTableContainer columns={gridColumns} isRefetching={isRefetching}>
				<thead>
					<PunchupHeaderRow>
						<PunchupColumnHeader
							sortable
							sortDirection={sortDir('date')}
							onSort={() => onSort('date')}
							onResize={(w) => setColumnWidth('date', w)}
						>
							{t('customFields-responses-table-date-green', 'Date')}
						</PunchupColumnHeader>
						<PunchupColumnHeader onResize={(w) => setColumnWidth('question', w)}>
							{t('viewResponses-table-question', 'Question')}
						</PunchupColumnHeader>
						<PunchupColumnHeader onResize={(w) => setColumnWidth('answer', w)}>
							{t('viewResponses-table-answer', 'Answer')}
						</PunchupColumnHeader>
						<PunchupColumnHeader
							sortable
							sortDirection={sortDir('full_name')}
							onSort={() => onSort('full_name')}
							onResize={(w) => setColumnWidth('fullName', w)}
						>
							{t('customFields-responses-table-fullName', 'Full Name')}
						</PunchupColumnHeader>
						<PunchupColumnHeader
							sortable
							sortDirection={sortDir('email')}
							onSort={() => onSort('email')}
							onResize={(w) => setColumnWidth('email', w)}
						>
							{t('customFields-responses-table-email', 'Email')}
						</PunchupColumnHeader>
						<PunchupColumnHeader onResize={(w) => setColumnWidth('phone', w)}>
							{t('customFields-responses-table-phone', 'Phone')}
						</PunchupColumnHeader>
					</PunchupHeaderRow>
				</thead>
				<tbody>
					{isLoading || data === undefined ? (
						<PunchupTableLoadingRow />
					) : flatRows.length === 0 ? (
						<PunchupTableEmptyRow text={t('customFields-responses-empty', 'No responses found')} />
					) : (
						flatRows.map((row) => (
							<PunchupDataRow key={row.key}>
								<PunchupDateTimeCell>{formatDate(row.date)}</PunchupDateTimeCell>
								<PunchupEntityCell>{row.question}</PunchupEntityCell>
								<td>{row.answer}</td>
								<PunchupNameCell>{row.fullName}</PunchupNameCell>
								<PunchupNameCell>{row.email}</PunchupNameCell>
								<PunchupNameCell>{row.phone}</PunchupNameCell>
							</PunchupDataRow>
						))
					)}
				</tbody>
			</PunchupTableContainer>

			<PunchupLoadMore isDisabled={!hasNextPage} isLoading={isFetchingNextPage} handleClick={() => fetchNextPage()} />
		</>
	);
};
